import { useEffect, useMemo, useState } from "react";
import { prettifyApiError } from "@/shared/api";
import { CopyButton } from "@/features/ordini/components/OrderCard";
import { fetchTracking, type TrackingEvent, type TrackingResult, type TrackingStep } from "../api";
import { ColliStato } from "./ColliStato";
import { nomeCorriere } from "@/features/ordini/nomeCorriere";
import { StepBadge } from "./StepBadge";
import {
  ChevronIcon,
  ClockIcon,
  ExternalIcon,
  formatTimestamp,
  RefreshIcon,
  RigaTracking,
  SpinnerIcon,
  WarningIcon,
} from "./trackingParti";

/** Portale di tracking Bracchi: non accetta il codice nell'URL, lo si incolla
 *  a mano (per questo accanto a ogni codice c'e' il tasto copia). */
const PORTALE_BRACCHI = "https://service.traspak.it/trackingbracchi";

/** Dove seguire a mano la spedizione. poste.it prende il codice nell'URL e
 *  legge anche le LDV SDA; il portale Bracchi no (si incolla). */
export function portaleTracking(fonte: string, code: string): { url: string; label: string } {
  if (fonte === "Bracchi") return { url: PORTALE_BRACCHI, label: "Apri il portale Bracchi" };
  return {
    url: `https://www.poste.it/cerca-spedizioni/index.html#/risultati-spedizioni/${encodeURIComponent(code)}`,
    label: "Apri su poste.it",
  };
}

interface TrackingPanelProps {
  /** Corriere assegnato (es. "SDA"). Vuoto/assente → non mostrato. */
  carrier: string;
  /** Codice tracking master (LDV) dell'ordine. Vuoto = nessuna LDV. */
  trackingCode: string;
  /** Codici dei colli della spedizione (possono coincidere col master). */
  parcelCodes: string[];
  /** Apre un URL nel browser di sistema (openExternalUrl del ContextPanel). */
  onOpenExternal: (url: string) => void;
  /** Pagina ordine: una riga (codice, stato, ultimo evento) e il resto su
   *  richiesta. Nel dialog e nei resi il pannello resta aperto. */
  compatto?: boolean;
  /** Avvisa chi ospita il pannello che il corriere dice consegnata (lettura
   *  dal vivo): la pagina ordine aggiorna lo stepper senza aspettare il job. */
  onConsegnata?: () => void;
  /** Riga compatta senza il codice: chi ospita il pannello lo mostra gia'. */
  nascondiCodice?: boolean;
}

/**
 * Corpo informativo dello storico spedizione (Piano 4, 2026-07-13): estratto
 * da TrackingHistoryDialog per essere riusato INLINE nella pagina ordine
 * (ShippingSection). Il dialog resta un guscio (scrim + focus-trap) che
 * renderizza questo panel. La fonte la sceglie il backend (Poste o Bracchi,
 * ``shared/tracking_fonte``) e la dice nella risposta:
 *  - fetch GET /api/tracking al mount (master, o colli deduplicati);
 *  - ogni apertura e ogni «Aggiorna» sono una lettura dal vivo;
 *  - con piu' colli, lo stato di ciascuno in tabella sopra la timeline;
 *  - interruttore spento: lo dice, invece di «non disponibile»;
 *  - corriere diverso da SDA (BRT, Poste Delivery): Bracchi non lo conosce,
 *    nessuna lettura e uno stato vuoto che lo dice.
 */
export function TrackingPanel({
  carrier,
  trackingCode,
  parcelCodes,
  onOpenExternal,
  compatto = false,
  onConsegnata,
  nascondiCodice = false,
}: TrackingPanelProps) {
  // Bracchi e' il concessionario SDA: un BRT o un Poste Delivery non li conosce
  // (spec §4.6), e interrogarlo darebbe un «non ancora preso in carico» falso
  // su un pacco magari consegnato. Vuoto = SDA, come il default degli ordini.
  const corriere = (carrier ?? "").trim().toUpperCase();
  // SDA e Poste Delivery: l'API Poste legge entrambe (28/09); Bracchi solo SDA
  // e per Poste risponde vuoto, che il pannello dice come «non disponibile».
  const corriereSda = !corriere || corriere.includes("SDA") || corriere.includes("POSTE");
  // Codici da interrogare: se abbiamo la LDV madre, SOLO quella. Bracchi
  // risponde per tutta la spedizione (colli ed eventi di ciascuno), quindi
  // interrogare anche i colli sarebbero N richieste inutili. I colli senza
  // LDV madre nota (raro) si interrogano per-code come fallback.
  const codes = useMemo(() => {
    if (!corriereSda) return [];
    const master = (trackingCode ?? "").trim();
    if (master) return [master];
    // Nessun LDV madre: deduplica i colli preservando l'ordine, cappa a 30.
    const seen = new Set<string>();
    const ordered: string[] = [];
    for (const raw of parcelCodes) {
      const code = (raw ?? "").trim();
      if (!code || seen.has(code)) continue;
      seen.add(code);
      ordered.push(code);
    }
    return ordered.slice(0, 30);
  }, [corriereSda, trackingCode, parcelCodes]);

  const masterCode = (trackingCode ?? "").trim();
  // I colli "veri" mostrati nel pannello: derivano dal prop ``parcelCodes``
  // (NON da ``codes`` che contiene solo la LDV madre quando presente). Senza
  // stato per collo da Bracchi li mostriamo con gli eventi della madre.
  const parcelOnlyCodes = useMemo(() => {
    const seen = new Set<string>();
    const ordered: string[] = [];
    for (const raw of parcelCodes) {
      const code = (raw ?? "").trim();
      if (!code || code === masterCode || seen.has(code)) continue;
      seen.add(code);
      ordered.push(code);
    }
    return ordered;
  }, [parcelCodes, masterCode]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resultsByCode, setResultsByCode] = useState<Map<string, TrackingResult>>(new Map());
  // false = interruttore OM_BRACCHI_TRACKING spento (lo dice il backend).
  const [attivo, setAttivo] = useState(true);
  // Chi legge il tracking, detto dal backend: prima della risposta non si sa.
  const [fonte, setFonte] = useState("");
  const [aperto, setAperto] = useState(!compatto);
  // Tasto "Aggiorna": bump di reloadKey ri-lancia l'effect.
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (codes.length === 0) {
      setLoading(false);
      return;
    }
    // Il ``signal`` va passato alla fetch, altrimenti l'abort del cleanup non
    // annulla nulla e la risposta di un tracking vecchio puo' arrivare dopo
    // quella nuova (cambio thread/ordine rapido). ``cancelled`` resta come
    // guardia del setState anche se apiGet ignorasse il signal (mock nei test).
    const controller = new AbortController();
    let cancelled = false;
    setLoading(true);
    setError(null);
    void fetchTracking(codes, controller.signal)
      .then((res) => {
        if (cancelled || controller.signal.aborted) return;
        const map = new Map<string, TrackingResult>();
        for (const r of res.results) map.set(r.code, r);
        setResultsByCode(map);
        setAttivo(res.attivo);
        setFonte(res.fonte ?? "");
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled || controller.signal.aborted) return;
        setError(prettifyApiError(err));
        setLoading(false);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [codes, reloadKey]);

  // Rilegge da Bracchi: utile quando il corriere ha preso in carico DOPO il
  // primo caricamento e il popup mostra ancora vuoto.
  function handleRefresh() {
    if (loading || codes.length === 0) return;
    setReloadKey((k) => k + 1);
  }

  const masterResult = masterCode ? resultsByCode.get(masterCode) : undefined;
  const consegnataDalVivo = masterResult?.current_step === "Consegnata";
  useEffect(() => {
    if (consegnataDalVivo) onConsegnata?.();
  }, [consegnataDalVivo, onConsegnata]);
  // Stato per collo solo quando i colli sono piu' d'uno: con uno solo la
  // tabella ripeterebbe la LDV principale.
  const colli = masterResult?.colli && masterResult.colli.length > 1 ? masterResult.colli : null;
  const risultati = Array.from(resultsByCode.values());
  // Madre o figlie: basta che una torni indietro.
  const ritorno = risultati.find((r) => r.ritorno_mittente);
  // La fonte si dichiara solo per una lettura dal vivo riuscita: a interruttore
  // spento gli eventi sono d'archivio (anche di epoca Poste), con Bracchi muto
  // o la fetch fallita non si e' letto nulla. Nemmeno prima della risposta o
  // senza codici: ``attivo`` parte vero, e una lettura non c'e' ancora stata.
  const dalVivo =
    !loading &&
    codes.length > 0 &&
    attivo &&
    !error &&
    !risultati.some((r) => r.stale || r.unavailable);
  // A spento «Aggiorna» ricarica l'archivio: Bracchi non viene interrogato.
  const titoloAggiorna =
    attivo && fonte ? `Aggiorna tracking: rilegge da ${fonte}` : "Aggiorna tracking";
  const haEventi = risultati.some((r) => r.events.length > 0);

  if (!corriereSda) {
    return (
      <CorriereSenzaTracking carrier={carrier} code={masterCode || parcelOnlyCodes[0] || ""} />
    );
  }

  const portale = portaleTracking(fonte, masterCode || parcelOnlyCodes[0] || "");
  if (compatto && !aperto) {
    return (
      <RigaTracking
        code={nascondiCodice ? "" : masterCode}
        result={masterResult}
        loading={loading}
        error={error}
        colli={parcelOnlyCodes.length + (masterCode ? 1 : 0)}
        titoloAggiorna={titoloAggiorna}
        onRefresh={handleRefresh}
        onApri={() => setAperto(true)}
        ritorno={ritorno}
      />
    );
  }

  return (
    <div>
      {/* Toolbar: sottotitolo + badge step master + Aggiorna. Nel dialog
          questi vivevano nell'header accanto a titolo/X; qui sono la prima
          riga del panel così il guscio non deve conoscere lo stato fetch. */}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-[11.5px] text-ink">
          Eventi reali del corriere{" "}
          {carrier ? <span className="font-semibold">{nomeCorriere(carrier)}</span> : null}
          {dalVivo && fonte ? ` · letti da ${fonte}` : null}
        </p>
        <div className="flex shrink-0 items-center gap-2">
          {masterResult && masterResult.current_step ? (
            <StepBadge step={masterResult.current_step} />
          ) : null}
          <button
            type="button"
            onClick={handleRefresh}
            disabled={loading || codes.length === 0}
            aria-label={titoloAggiorna}
            title={titoloAggiorna}
            className="inline-flex items-center gap-1.5 rounded-sm border border-line bg-card px-2.5 py-1.5 text-[11.5px] font-medium text-ink enabled:hover:border-accent enabled:hover:bg-paper disabled:cursor-not-allowed disabled:bg-paper-2"
          >
            <RefreshIcon spinning={loading} />
            Aggiorna
          </button>
          {compatto ? (
            <button
              type="button"
              onClick={() => setAperto(false)}
              className="inline-flex items-center gap-1 rounded-sm border border-line bg-card px-2.5 py-1.5 text-[11.5px] font-medium text-ink hover:border-accent hover:bg-paper"
            >
              Chiudi dettaglio
            </button>
          ) : null}
        </div>
      </div>

      <div className="space-y-5">
        {loading ? (
          <LoadingBlock />
        ) : codes.length === 0 ? (
          <EmptyTracking
            code=""
            label="Nessun codice di tracking associato a questo ordine."
            portale={portale}
            onOpenExternal={onOpenExternal}
          />
        ) : (
          <>
            {error ? <FetchErrorNotice error={error} /> : null}
            {!attivo ? (
              <p
                role="status"
                className="rounded-sm border border-line bg-paper-2 px-3 py-2 text-[12.5px] font-semibold text-ink"
              >
                Lettura dal vivo del tracking spenta:{" "}
                {haEventi
                  ? "qui sotto l'ultimo stato in archivio."
                  : "nessun evento in archivio per questa spedizione."}
              </p>
            ) : null}
            {/* Con piu' colli lo stato di ciascuno viene prima della timeline:
                uno consegnato e l'altro fermo e' la cosa da vedere subito. */}
            {ritorno ? <AvvisoRitorno result={ritorno} /> : null}
            {colli ? <ColliStato colli={colli} /> : null}
            {masterCode ? (
              <TrackingSection
                title="LDV principale"
                code={masterCode}
                result={masterResult}
                emphasized
                spento={!attivo}
                fonte={fonte}
                portale={portale}
                onOpenExternal={onOpenExternal}
              />
            ) : null}
            {!colli && parcelOnlyCodes.length > 0 ? (
              <section>
                <SectionTitle>Colli ({parcelOnlyCodes.length})</SectionTitle>
                {masterCode ? (
                  // Con la LDV madre gli eventi sono i suoi: per collo resta
                  // solo il codice da copiare, in griglia (16 schede da 56px
                  // l'una riempivano la pagina ordine, 28/09).
                  <ul className="grid grid-cols-1 gap-x-3 gap-y-1 sm:grid-cols-2 xl:grid-cols-3">
                    {parcelOnlyCodes.map((code) => (
                      <li key={code} className="flex min-w-0 items-center gap-1">
                        <span className="truncate font-mono text-[11.5px] text-ink" title={code}>
                          {code}
                        </span>
                        <CopyButton text={code} label="Copia codice collo" />
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="space-y-2.5">
                    {parcelOnlyCodes.map((code) => (
                      <CollapsibleParcel
                        key={code}
                        code={code}
                        result={resultsByCode.get(code)}
                        spento={!attivo}
                        fonte={fonte}
                        portale={portaleTracking(fonte, code)}
                        onOpenExternal={onOpenExternal}
                      />
                    ))}
                  </div>
                )}
              </section>
            ) : null}
          </>
        )}

        <p className="border-t border-line pt-3 text-[10.5px] leading-snug text-ink">
          {dalVivo && fonte
            ? `I passaggi mostrati provengono dal tracking ${fonte} e possono`
            : "I passaggi mostrati possono"}{" "}
          avere un ritardo rispetto al movimento reale del pacco.
        </p>
      </div>
    </div>
  );
}

/** Sezione timeline per un codice (master o collo aperto). */
function TrackingSection({
  title,
  code,
  result,
  emphasized = false,
  spento,
  fonte,
  portale,
  onOpenExternal,
}: {
  title?: string;
  code: string;
  result: TrackingResult | undefined;
  emphasized?: boolean;
  spento: boolean;
  fonte: string;
  portale: { url: string; label: string };
  onOpenExternal: (url: string) => void;
}) {
  const events = result?.events ?? [];
  return (
    <section>
      {title ? <SectionTitle>{title}</SectionTitle> : null}
      <div
        className={[
          "rounded-md border bg-paper px-4 py-3.5",
          emphasized ? "border-line-2" : "border-line",
        ].join(" ")}
      >
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-1.5">
            {/* Codice GRANDE + copia a un click (Davide 15/07: «ben visibile
                e facilmente copiabile», qui e nella pagina ordine — il panel
                e' condiviso). */}
            <span className="truncate font-mono text-[14px] font-semibold text-ink" title={code}>
              {code}
            </span>
            <CopyButton text={code} label="Copia codice tracking" />
            {result?.product ? (
              <span className="shrink-0 text-[10.5px] uppercase tracking-[0.06em] text-ink">
                {result.product}
              </span>
            ) : null}
          </div>
          {result?.current_step ? <StepBadge step={result.current_step} /> : null}
        </div>

        {result?.eta ? (
          <div className="mb-3 flex items-center gap-1.5 rounded-sm bg-accent-soft px-2.5 py-1.5 text-[11.5px] text-ink">
            <ClockIcon />
            <span>{result.eta}</span>
          </div>
        ) : null}

        {/* Timeline dall'archivio: quello che sapevamo lo sappiamo ancora.
            Mostrarlo con la sua data e' piu' utile di "non disponibile" — a
            patto di non spacciarlo per aggiornato, da cui la riga esplicita.
            A interruttore spento nessuno ha interrogato il corriere, quindi
            «non risponde» sarebbe falso: lo dice l'archivio. */}
        {result?.stale ? (
          <div className="mb-3 flex items-center gap-1.5 rounded-sm border border-line bg-paper-2 px-2.5 py-1.5 text-[11px] text-ink">
            <ClockIcon />
            <span>
              {spento
                ? `Dall'archivio, ultimo aggiornamento il ${formatTimestamp(result.updated_at ?? "")} · lettura dal vivo spenta`
                : `Ultimo aggiornamento ${formatTimestamp(result.updated_at ?? "")} · ${fonte || "il corriere"} non risponde in questo momento`}
            </span>
          </div>
        ) : null}

        {events.length > 0 ? (
          <EventTimeline events={events} />
        ) : (
          <EmptyTracking
            code={code}
            unavailable={result?.unavailable}
            spento={spento}
            fonte={fonte}
            portale={portale}
            onOpenExternal={onOpenExternal}
          />
        )}
      </div>
    </section>
  );
}

/** Collo collassabile: header con codice + badge step, click espande la
 *  timeline. Il primo collo non viene aperto di default (lo si scansiona
 *  per badge); l'utente espande quello che gli interessa. */
function CollapsibleParcel({
  code,
  result,
  spento,
  fonte,
  portale,
  onOpenExternal,
}: {
  code: string;
  result: TrackingResult | undefined;
  spento: boolean;
  fonte: string;
  portale: { url: string; label: string };
  onOpenExternal: (url: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const events = result?.events ?? [];
  const latest = events.length > 0 ? events[events.length - 1] : undefined;
  return (
    <div className="overflow-hidden rounded-md border border-line bg-paper">
      {/* Header = flex di [toggle][copia][badge]: il bottone copia NON puo'
          stare dentro il toggle (button annidato in button = HTML invalido). */}
      <div className="flex w-full items-center gap-1.5 pr-3.5 hover:bg-paper-2">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="flex min-w-0 flex-1 items-center gap-2.5 px-3.5 py-2.5 text-left"
        >
          <ChevronIcon open={open} />
          <span className="min-w-0 flex-1">
            <span
              className="block truncate font-mono text-[13px] font-semibold text-ink"
              title={code}
            >
              {code}
            </span>
            {latest ? (
              <span className="mt-0.5 block truncate text-[10.5px] text-ink" title={latest.status}>
                {latest.status}
              </span>
            ) : (
              <span className="mt-0.5 block text-[10.5px] text-ink">
                {spento
                  ? "Nessun evento in archivio"
                  : result?.unavailable
                    ? "Servizio non disponibile"
                    : "Tracking non ancora disponibile"}
              </span>
            )}
          </span>
        </button>
        <CopyButton text={code} label="Copia codice collo" />
        <StepBadge step={result?.current_step ?? ""} />
      </div>
      {open ? (
        <div className="border-t border-line px-4 py-3.5">
          {events.length > 0 ? (
            <EventTimeline events={events} />
          ) : (
            <EmptyTracking
              code={code}
              unavailable={result?.unavailable}
              spento={spento}
              fonte={fonte}
              portale={portale}
              onOpenExternal={onOpenExternal}
            />
          )}
        </div>
      ) : null}
    </div>
  );
}

/** Timeline verticale degli eventi: piu' RECENTE in alto (evidenziato come
 *  stato corrente), piu' vecchi sotto. Testo nero per tutti (Davide 23/09):
 *  il corrente si distingue per grassetto, badge e pallino colorato, non per
 *  un grigio o un'opacita'. Gli eventi arrivano ASC dal backend: invertiamo
 *  per renderizzare newest-first. */
function EventTimeline({ events }: { events: TrackingEvent[] }) {
  const desc = useMemo(() => [...events].reverse(), [events]);
  return (
    <ol className="relative">
      {desc.map((event, i) => {
        const isCurrent = i === 0;
        const isLast = i === desc.length - 1;
        // L'evento del ritorno resta rosso anche quando ne arrivano altri dopo.
        const ritorno = event.step === "In restituzione";
        return (
          <li key={`${event.timestamp}-${i}`} className="flex gap-3">
            {/* Colonna marker + connettore verticale. */}
            <div className="flex flex-col items-center">
              <span
                className={[
                  "mt-1 inline-flex h-2.5 w-2.5 shrink-0 rounded-full",
                  isCurrent || ritorno ? stepDotClass(event.step) : "bg-ink-4",
                ].join(" ")}
                aria-current={isCurrent ? "step" : undefined}
              />
              {!isLast ? (
                <span className="my-0.5 w-[2px] flex-1 rounded-full bg-line" aria-hidden="true" />
              ) : null}
            </div>
            <div className="min-w-0 pb-4">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={[
                    "text-[12.5px] leading-snug",
                    isCurrent || ritorno ? "font-semibold text-ink" : "font-normal text-ink",
                  ].join(" ")}
                >
                  {event.status}
                </span>
                {(isCurrent || ritorno) && event.step ? (
                  <StepBadge step={event.step} small />
                ) : null}
              </div>
              <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 font-mono text-[10.5px] text-ink">
                <span>{formatTimestamp(event.timestamp)}</span>
                {event.location ? (
                  <>
                    <span aria-hidden="true">·</span>
                    <span>{event.location}</span>
                  </>
                ) : null}
                {/* Spedizione a piu' colli: la timeline li mescola, quindi
                    ogni evento dice di quale collo e'. */}
                {event.collo ? (
                  <>
                    <span aria-hidden="true">·</span>
                    <span className="font-semibold">Collo {event.collo}</span>
                  </>
                ) : null}
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/** Stato vuoto onesto: nessun evento per questo codice. Tre casi da non
 *  confondere: ``spento`` = interruttore Bracchi spento (nessuna lettura dal
 *  vivo, lo spiega l'avviso in cima); ``unavailable`` = Bracchi non ha
 *  risposto; altrimenti la spedizione non e' ancora stata presa in carico. */
function EmptyTracking({
  code,
  label,
  unavailable = false,
  spento = false,
  fonte = "",
  portale,
  onOpenExternal,
}: {
  code: string;
  label?: string;
  unavailable?: boolean;
  spento?: boolean;
  fonte?: string;
  portale: { url: string; label: string };
  onOpenExternal: (url: string) => void;
}) {
  let title = "Tracking non ancora disponibile";
  let subtitle = "La spedizione non è ancora stata presa in carico dal corriere.";
  if (spento) {
    title = "Nessun evento in archivio";
    subtitle = "Puoi seguire la spedizione sul sito del corriere.";
  } else if (unavailable) {
    title = "Tracking momentaneamente non disponibile";
    subtitle = `${fonte || "Il corriere"} non ha risposto: riprova tra poco.`;
  }
  return (
    <div className="rounded-sm border border-dashed border-line bg-card px-3 py-3 text-center text-ink">
      <p className="text-[12px] font-semibold">{label ?? title}</p>
      <p className="mt-0.5 text-[10.5px]">{subtitle}</p>
      {code ? (
        <button
          type="button"
          onClick={() => onOpenExternal(portale.url)}
          className="mt-2 inline-flex items-center gap-1.5 rounded-sm border border-line bg-card px-2.5 py-1.5 text-[11.5px] font-medium text-ink hover:border-accent hover:bg-paper"
        >
          <ExternalIcon />
          {portale.label}
        </button>
      ) : null}
    </div>
  );
}

/** Corriere diverso da SDA: Bracchi non lo conosce e OM non ha un'altra
 *  fonte, quindi nessuna lettura e nessun rimando al portale Bracchi. Il
 *  codice resta visibile e copiabile (nella pagina ordine e' l'unica riga
 *  che lo mostra). */
function CorriereSenzaTracking({ carrier, code }: { carrier: string; code: string }) {
  return (
    <div className="rounded-sm border border-dashed border-line bg-card px-3 py-3 text-ink">
      {code ? (
        <div className="mb-1.5 flex min-w-0 items-center gap-1.5">
          <span className="truncate font-mono text-[14px] font-semibold" title={code}>
            {code}
          </span>
          <CopyButton text={code} label="Copia codice tracking" />
        </div>
      ) : null}
      <p className="text-[12px] font-semibold">
        Tracking non disponibile in OM per {nomeCorriere(carrier)}
      </p>
      <p className="mt-0.5 text-[10.5px]">
        OM legge le spedizioni SDA e Poste Delivery: segui questa sul sito del corriere.
      </p>
    </div>
  );
}

/** L'intera fetch e' fallita: riga d'errore + invito ad aprire il portale
 *  Bracchi. Non blocca il resto del rendering (le sezioni mostreranno
 *  comunque l'empty state con link). */
function FetchErrorNotice({ error }: { error: string }) {
  return (
    <div
      role="alert"
      className="flex items-start gap-2 rounded-sm border border-red/20 bg-red-soft px-3 py-2.5 text-[12px] text-ink"
    >
      <span className="mt-0.5 shrink-0">
        <WarningIcon />
      </span>
      <span>
        Impossibile recuperare il tracking in tempo reale ({error}). Puoi comunque seguire la
        spedizione sul sito del corriere.
      </span>
    </div>
  );
}

function LoadingBlock() {
  return (
    <div className="flex items-center gap-2.5 px-1 py-6 text-[12.5px] text-ink">
      <SpinnerIcon />
      <span>Recupero gli eventi di tracking…</span>
    </div>
  );
}

/** Banda rossa del ritorno al mittente (26/10): in cima, prima dei colli e
 *  della timeline, perche' dopo il ritorno gli eventi tornano «in transito» e
 *  «consegnata» e da soli non lo dicono piu'. */
export function AvvisoRitorno({ result }: { result: TrackingResult }) {
  const rientrata = result.current_step === "Rientrata al mittente";
  const quanti = (result.colli ?? []).filter((c) => c.ritorno_mittente).length;
  const totali = (result.colli ?? []).length;
  return (
    <div
      role="alert"
      className="flex items-start gap-2 rounded-md border-2 border-red bg-red-soft px-3 py-2.5 text-ink"
    >
      <span className="mt-0.5 shrink-0 text-red">
        <WarningIcon />
      </span>
      <span className="min-w-0 text-[12.5px] leading-snug">
        <span className="block font-extrabold">
          {rientrata ? "Rientrata al mittente" : "Ritorno al mittente"}
        </span>
        {rientrata
          ? "Il pacco è tornato indietro: la «consegnata» del corriere non è al cliente."
          : "Il corriere sta riportando indietro il pacco."}
        {totali > 1 && quanti < totali ? ` Colli che tornano: ${quanti} di ${totali}.` : null}
      </span>
    </div>
  );
}

/** Colore del pallino dell'evento corrente in timeline, allineato al badge. */
function stepDotClass(step: TrackingStep): string {
  switch (step) {
    case "In transito":
      return "bg-blue";
    case "In consegna":
      return "bg-amber";
    case "Consegnata":
      return "bg-green";
    case "In giacenza":
    case "In restituzione":
    case "Ritorno al mittente":
    case "Rientrata al mittente":
      return "bg-red";
    case "Presa in carico":
      return "bg-ink-2";
    default:
      return "bg-accent";
  }
}

/** Titolo di sezione, uguale a quello della tabella colli. */
function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h4 className="mb-1.5 text-[12.5px] font-extrabold text-ink">{children}</h4>;
}
